import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './SWMSPCBUComplianceSoftware.css';

const SWMS_FEATURES = [
  {
    title: 'Centralise SWMS and WHS Documents',
    icon: 'folder',
    paragraphs: [
      'Store SWMS, risk assessments, WHS plans, policies, procedures and registers in a searchable document library. Documents can include version information, review dates and expiry dates, helping teams identify documents that need attention.',
    ],
  },
  {
    title: 'Connect Documents With Daily Compliance Work',
    icon: 'link',
    paragraphs: [
      'A SWMS can sit alongside digital forms, inspections, hazards, incidents, corrective actions and tasks. This gives teams a way to connect documentation with the work being completed on site rather than managing each record separately.',
    ],
  },
  {
    title: 'Use Digital WHS Forms',
    icon: 'form',
    paragraphs: [
      "Complimate's Digital Forms & QR functionality allows workers to access forms from mobile devices, complete dynamic fields and capture signatures. Forms can be linked to sites, projects, employees and assets, with completed records available for review and PDF export.",
    ],
  },
  {
    title: 'Track Actions From Findings',
    icon: 'task',
    paragraphs: [
      'If an inspection or audit identifies an issue, the broader Complimate workflow can support corrective actions and tasks, including assigning responsibility and tracking completion.',
    ],
  },
];

const FEATURE_TABLE = [
  {
    capability: 'Document Management',
    icon: 'folder',
    helps: 'Store SWMS, policies, procedures, risk assessments and registers',
  },
  {
    capability: 'Digital Forms & QR',
    icon: 'form',
    helps: 'Capture site forms, inspections, inductions and other records digitally',
  },
  {
    capability: 'Audits & Inspections',
    icon: 'audit',
    helps: 'Record findings and compliance checks',
  },
  {
    capability: 'Corrective Actions',
    icon: 'task',
    helps: 'Assign and track actions arising from findings',
  },
  {
    capability: 'Tasks',
    icon: 'project',
    helps: 'Give compliance work clear owners and status',
  },
  {
    capability: 'Sites',
    icon: 'pin',
    helps: 'Maintain site records and link site documents and forms',
  },
  {
    capability: 'Assets & Registers',
    icon: 'plant',
    helps: 'Track vehicles, plant, equipment and related information',
  },
  {
    capability: 'AI Compliance Assistant',
    icon: 'spark',
    helps:
      'Ask questions using uploaded documents and support drafting of policies, risk assessments and SWMS',
  },
];

const WORKFLOW_STEPS = [
  {
    title: 'Upload your current documents',
    icon: 'upload',
    text: 'Add SWMS, risk assessments, policies, procedures and other WHS records.',
  },
  {
    title: 'Organise documents by your business needs',
    icon: 'folder',
    text: 'Use the document library and relevant site or project information to keep records organised.',
  },
  {
    title: 'Complete WHS activities digitally',
    icon: 'form',
    text: 'Use digital forms, QR codes and mobile access for relevant site activities.',
  },
  {
    title: 'Record findings and issues',
    icon: 'hazard',
    text: 'Capture hazards, inspection findings or other compliance information.',
  },
  {
    title: 'Assign corrective actions and tasks',
    icon: 'task',
    text: 'Give responsible team members clear actions to complete.',
  },
  {
    title: 'Review and maintain records',
    icon: 'audit',
    text: 'Keep track of documents, actions and compliance activity from one platform.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'What is a SWMS?',
    answer:
      'A Safe Work Method Statement is a document that identifies high risk construction work, the hazards and risks associated with that work, and the control measures to be implemented.',
  },
  {
    question: 'Does every construction job require a SWMS?',
    answer:
      'No. Under the model WHS framework, SWMS requirements apply to high risk construction work. Other construction work may not require a SWMS, although PCBUs still have duties to manage health and safety risks.',
  },
  {
    question: 'Can Complimate store SWMS?',
    answer:
      "Yes. Complimate's document management functionality supports storage and organisation of SWMS, risk assessments, WHS plans, policies, procedures and registers, including version and review/expiry information.",
  },
  {
    question: 'Can Complimate help with PCBU compliance?',
    answer:
      'Complimate can help businesses organise documents, forms, inspections, actions, tasks and other compliance activities that support their WHS management system. It does not itself make a business legally compliant or replace professional WHS advice.',
  },
  {
    question: 'Can workers access WHS forms on mobile?',
    answer:
      'Yes. Complimate provides Android and iOS mobile apps, and its digital forms can be accessed through mobile devices and QR codes.',
  },
  {
    question: 'Does Complimate provide WHS legal advice?',
    answer:
      'No. Complimate provides software and AI-assisted compliance functionality. Compliance information and AI-generated content should be reviewed against the requirements that apply to your business and jurisdiction.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/swms-pcbu-compliance/');

const ICON_PATHS = {
  hardhat: (
    <>
      <path d="M4 14a8 8 0 0 1 16 0M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3ZM12 4v3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />
      <path d="m8.5 12 2.2 2.2 4.8-5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93" />
      <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  audit: (
    <>
      <path d="M8 4h8M9 3v3h6V3M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  project: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2v4M16 2v4M3 9h18M7 13h4M7 17h8" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3 1.3 4.2L17.5 9l-4.2 1.8L12 15l-1.3-4.2L6.5 9l4.2-1.8L12 3Z" />
      <path d="m19 15 .6 1.9 1.9.6-1.9.6L19 21l-.6-1.9-1.9-.6 1.9-.6L19 15Z" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4M8 8l4-4 4 4" />
      <path d="M5 13v7h14v-7" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5M12 17h.01" />
    </>
  ),
  duty: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="swms-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.shield}
      </svg>
    </span>
  );
}

function SWMSPCBUComplianceSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="swms-page">
        <section className="swms-hero">
          <Container>
            <div className="swms-hero-grid">
              <div className="swms-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="hardhat" />
                  SWMS &amp; PCBU Compliance Software
                </p>
                <h1 className="seo-h1">SWMS &amp; PCBU Compliance Software</h1>
                <p className="seo-lead">
                  Complimate helps Australian businesses manage Safe Work Method
                  Statements (SWMS), PCBU responsibilities, risk assessments and
                  workplace safety documentation through a central{' '}
                  <Link
                    to={APP_URLS.WHS_COMPLIANCE_SOFTWARE}
                    className="section-description-link"
                  >
                    WHS compliance software
                  </Link>
                  {' '}platform.
                </p>
                <p className="seo-body">
                  For businesses working under model WHS laws, Complimate can
                  support the systems and records used to manage PCBU
                  responsibilities. It does not replace your legal duties,
                  professional WHS advice or the requirements of your state or
                  territory regulator.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us/"
                    className="btn-teal swms-primary-button"
                    id="swms_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>
              <div className="swms-hero-visual">
                <div className="swms-visual-glow" aria-hidden="true" />
                <div className="swms-product-window">
                  <div className="swms-window-bar" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate dashboard for SWMS documents, WHS tasks and compliance records"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="swms-float-card swms-float-card-top" aria-hidden="true">
                  <FeatureIcon name="folder" />
                  <span>SWMS documents</span>
                </div>
                <div className="swms-float-card swms-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="task" />
                  <span>Compliance tasks</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section swms-intro-section">
          <Container>
            <div className="swms-intro-grid">
              <div>
                <div className="swms-section-marker"><FeatureIcon name="shield" /></div>
                <h2 className="seo-h2">
                  What Is a Safe Work Method Statement (SWMS)?
                </h2>
                <p className="seo-body">
                  A Safe Work Method Statement (SWMS) is a document used for high
                  risk construction work. It identifies the high risk construction
                  activities, the hazards and risks associated with those
                  activities, and the control measures to be implemented.
                </p>
                <p className="seo-body">
                  For high risk construction work, a PCBU has additional duties
                  relating to preparing, keeping, complying with and reviewing the
                  SWMS. The SWMS should be available to relevant people involved in
                  the work and provided to the principal contractor where required.
                </p>
                <p className="seo-body mb-0">
                  A SWMS is not simply a document to file away. It should help
                  supervisors and workers understand the controls that apply to the
                  work and support the monitoring and review of those controls.
                </p>
              </div>
              <div className="swms-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate document library for SWMS and WHS records"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section swms-help-section">
          <Container>
            <div className="swms-help-intro">
              <div className="swms-shot-frame">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate SWMS document and digital form workflow"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="swms-section-marker"><FeatureIcon name="folder" /></div>
                <h2 className="seo-h2">
                  How Does Complimate Help With SWMS Management?
                </h2>
                <p className="seo-body">
                  Complimate supports{' '}
                  <Link
                    to={APP_URLS.DIGITAL_WHS_FORMS}
                    className="section-description-link"
                  >
                    digital WHS forms
                  </Link>
                  {' '}for safety checks, site inspections, hazard reporting and other
                  compliance activities.
                </p>
              </div>
            </div>
            <div className="swms-help-grid">
              {SWMS_FEATURES.map((item, index) => (
                <article className="swms-help-card" key={item.title}>
                  <div className="swms-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 48)} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section swms-pcbu-section">
          <Container>
            <div className="swms-pcbu-card">
              <div className="swms-section-marker"><FeatureIcon name="duty" /></div>
              <h2 className="seo-h2">
                What Are PCBU Responsibilities Under WHS Laws?
              </h2>
              <div className="swms-pcbu-copy">
                <p className="seo-body">
                  Under the model WHS laws, a PCBU (person conducting a business or
                  undertaking) has a primary duty to ensure, so far as is reasonably
                  practicable, the health and safety of workers and other people who
                  may be affected by the work.
                </p>
                <p className="seo-body">
                  PCBU responsibilities can include providing safe systems of work,
                  managing risks, providing information and training, maintaining
                  safe plant and workplaces, and consulting with workers and other
                  relevant duty holders.
                </p>
                <p className="seo-body">
                  In construction, there can be multiple duty holders, including
                  PCBUs carrying out construction work, principal contractors and
                  those managing or controlling workplaces. A business cannot simply
                  transfer its WHS duties to another party by subcontracting work.
                </p>
              </div>
              <div className="swms-note">
                <FeatureIcon name="info" />
                <p className="seo-body mb-0">
                  <strong>Important:</strong> Australian WHS requirements vary by
                  jurisdiction. The term PCBU is used within the model WHS
                  framework, while Victoria operates under its own Occupational
                  Health and Safety Act 2004 and associated regulations.
                  Businesses should check the legislation and regulator
                  requirements that apply to their location and work.
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section swms-features-section">
          <Container>
            <div className="swms-section-intro">
              <h2 className="seo-h2">
                Which Complimate Features Support WHS Compliance?
              </h2>
            </div>
            <div className="seo-table-wrap swms-spec-table">
              <table className="seo-table">
                <thead>
                  <tr>
                    <th scope="col">Complimate capability</th>
                    <th scope="col">How it can support your WHS system</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_TABLE.map((row) => (
                    <tr key={row.capability}>
                      <th scope="row">
                        <span className="swms-table-feature">
                          <FeatureIcon name={row.icon} />
                          {row.capability}
                        </span>
                      </th>
                      <td>{row.helps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Container>
        </section>

        <section className="seo-section swms-workflow-section">
          <Container>
            <div className="swms-section-intro">
              <h2 className="seo-h2">
                How Can You Manage SWMS and Compliance in Complimate?
              </h2>
            </div>
            <div className="swms-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="swms-step">
                  <div className="swms-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">{step.title}</h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section swms-cta-section">
          <Container>
            <div className="swms-cta-card">
              <span className="swms-cta-orb swms-cta-orb-one" aria-hidden="true" />
              <span className="swms-cta-orb swms-cta-orb-two" aria-hidden="true" />
              <div className="swms-cta-copy">
                <h2 className="seo-h2">
                  Manage SWMS and WHS Compliance in One Place
                </h2>
                <p className="seo-body">
                  Bring SWMS, WHS documents, digital forms, inspections, corrective
                  actions and compliance tasks together with Complimate.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us/"
                className="btn-teal swms-cta-button"
                id="swms_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section swms-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <p className="seo-faq-answer seo-body">{item.answer}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default SWMSPCBUComplianceSoftware;
